import { Injectable } from '@angular/core';
import { Resolve } from '@angular/router';
import { DummyService } from '@app/core/Services';
import { environment } from '@environments/environment';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import jwt_decode from 'jwt-decode';
import { CookieService } from 'ngx-cookie-service';
import { take } from 'rxjs/operators';

@Injectable()
export class AuthResolverService implements Resolve<any> {
  isLoggedUser: boolean;
  
  private static readonly MODULE_ID = '49';

  constructor(
    private dummyService: DummyService,
    private cookieService: CookieService,
    private oidcSecurityService: OidcSecurityService
  ) {}

  private waitForAuthReady(): Promise<boolean> {
    return this.oidcSecurityService
      .checkAuth()
      .pipe(take(1))
      .toPromise()
      .then((result: any) => !!result?.isAuthenticated);
  }

  private async getToken(): Promise<string | null> {
    const cookieToken = this.cookieService.get('newToken');
    if (cookieToken) {
      return cookieToken;
    }

    const oidcToken = await this.oidcSecurityService
      .getAccessToken()
      .pipe(take(1))
      .toPromise();

    return oidcToken || null;
  }

  private isSubscribed(decodedToken: any): boolean {
    const moduleIds = decodedToken?.ModuleIds || decodedToken?.moduleIds || '';

    if (!moduleIds || !AuthResolverService.MODULE_ID) {
      return true;
    }

    return moduleIds
      .split(',')
      .some((id: string) => id.trim() === AuthResolverService.MODULE_ID);
  }

  private async validateToken(): Promise<{
    isSubscribed: boolean;
    isExpired: boolean;
  }> {
    const token = await this.getToken();

    if (!token) {
      return { isSubscribed: false, isExpired: true };
    }

    try {
      const decodedToken: any = jwt_decode(token);

      return {
        isSubscribed: this.isSubscribed(decodedToken),
        isExpired: !decodedToken.exp || decodedToken.exp * 1000 <= Date.now(),
      };
    } catch {
      return { isSubscribed: false, isExpired: true };
    }
  }

  private clearSession(): void {
    localStorage.clear();
    sessionStorage.clear();
    this.cookieService.deleteAll('/');
  }

  private async logoffAndClear(): Promise<void> {
    try {
      await this.oidcSecurityService
        .logoff()
        .pipe(take(1))
        .toPromise();
    } catch {
    } finally {
      this.clearSession();
    }
  }

  private async refreshAccessToken(): Promise<boolean> {
    if (
      this.cookieService.get('isRefreshToken') !== '' &&
      this.cookieService.get('isRefreshToken') !== 'false'
    ) {
      await this.logoffAndClear();
      this.isLoggedUser = false;
      return false;
    }

    this.cookieService.set('isRefreshToken', 'true');

    try {
      const cookieRefreshToken = this.cookieService.get('refreshToken');
      const refreshToken = cookieRefreshToken
        ? cookieRefreshToken
        : await this.oidcSecurityService
            .getRefreshToken()
            .pipe(take(1))
            .toPromise();

      if (!refreshToken) {
        await this.logoffAndClear();
        this.isLoggedUser = false;
        return false;
      }

      const response = await this.dummyService
        .getTokenUsingRefreshToken(refreshToken, environment.clientId)
        .pipe(take(1))
        .toPromise();

      this.cookieService.set('isRefreshToken', 'false');
      this.cookieService.set('newToken', response.access_token);
      this.cookieService.set('refreshToken', response.refresh_token);
      this.isLoggedUser = true;
      return true;
    } catch {
      this.cookieService.set('isRefreshToken', 'false');
      await this.logoffAndClear();
      this.isLoggedUser = false;
      return false;
    }
  }

  async resolve(): Promise<any> {
    try {
      const isAuthenticated = await this.waitForAuthReady();

      if (!isAuthenticated) {
        this.isLoggedUser = false;
        return false;
      }

      const { isSubscribed, isExpired } = await this.validateToken();

      if (!isSubscribed) {
        await this.logoffAndClear();
        this.isLoggedUser = false;
        return false;
      }

      if (isExpired) {
        return this.refreshAccessToken();
      }

      this.isLoggedUser = true;
      return true;
    } catch {
      return this.refreshAccessToken();
    }
  }
}
